/* MailBakes - the recoverable card link.
   ---------------------------------------------------------------------------
   THIS FILE IS THE SPEC. It is shared, byte for byte, by every theme that
   carries the card configurator, so a link made on one theme restores on
   another. Change the format here and only here, bump VERSION, and keep
   decode() able to read every version that has ever been emailed out.

   A link is the product URL plus ?config=<payload>, where the payload is
   base64url(JSON) of this object. Keys are short on purpose: the link goes
   into emails, Klaviyo events and a cart attribute.

     v   2                        format version (v1 links still decode; they
                                  simply carry no m/mt)
     s   size id                  "one", a slices-product variant id, or "upgrade"
     f   ["Victoria Sponge", ...] one flavour NAME per slice, repeats allowed
     fo  font id                  handleized font name, e.g. "handwritten"
     c   colour id                handleized ink name, e.g. "deep-pink"
     w   "asap" | "pick"          when it should land
     d   "YYYY-MM-DD"             chosen date, only when w is "pick"
     x   { "<variant id>": qty }  extras, keyed by variant id
     p   delivery variant id      postage service, a variant of the Delivery product
     b   1                        "leave the message blank" was ticked
     q   1..10                    optional card quantity; absent means one
     st  0..6                     highest configurator step completed
                                  (1 cake, 2 flavours, 3 message, 4 delivery_date,
                                   5 postage, 6 extras)

     m   "Happy birthday..."      the card message (v2), at most MAX_MSG characters,
                                  newlines kept, absent when blank
     mt  1                        (v2) m had to be shortened so the whole URL
                                  stayed under MAX_URL characters

   The message rides in the link so an email can bring a customer back to a
   finished card. It is never a named property on a Klaviyo event; it reaches
   Klaviyo only inside restore_url. The same-device copy in localStorage is
   the fallback when a link arrives without it (a v1 link, or one that had to
   drop it to fit). Photos cannot be carried at all.

   The optional mb_edit=<cart line key> parameter sits alongside config and
   wins when that line is still in the basket, so a customer who already
   added the card edits it rather than building a second one.

   localStorage: one record per product under mb_flow:v1:<handle>, holding
   { t: ms, cfg: <the object above, plus m for the message>, step: <screen> }.
   Anything older than TTL_DAYS is ignored and cleared. */
(function () {
  'use strict';

  var VERSION = 2;
  var PARAM = 'config';
  var TTL_DAYS = 7;
  var MAX_MSG = Infinity; /* Link length is bounded below; local drafts keep the full message. */
  var MAX_URL = 2000;     /* the safe length for a link in an email */
  var STORE_PREFIX = 'mb_flow:v1:';
  var LINK_KEYS = ['v', 's', 'f', 'fo', 'c', 'w', 'd', 'x', 'p', 'b', 'st', 'm', 'mt', 'q', 'al', 'ts', 'vi', 'au'];
  var STORE_KEYS = LINK_KEYS;

  /* ---- base64url with real UTF-8 (flavour names carry & and accents) ---- */

  function toB64url(str) {
    var bytes;
    try {
      bytes = unescape(encodeURIComponent(str));
    } catch (e) {
      bytes = str;
    }
    return btoa(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function fromB64url(s) {
    var b64 = String(s || '').replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    var bytes = atob(b64);
    try {
      return decodeURIComponent(escape(bytes));
    } catch (e) {
      return bytes;
    }
  }

  /* Only the keys the spec knows, only when they carry something. */
  function pick(obj, keys) {
    var out = { v: VERSION };
    if (!obj || typeof obj !== 'object') return out;
    keys.forEach(function (k) {
      if (k === 'v') return;
      var val = obj[k];
      if (val == null || val === '' || val === false || val === 0) return;
      if (Array.isArray(val) && !val.length) return;
      if (typeof val === 'object' && !Array.isArray(val) && !Object.keys(val).length) return;
      out[k] = val;
    });
    return out;
  }

  /* Only ever whole characters: a surrogate pair is never cut in half. */
  function trimMessage(m, n) {
    var out = String(m).slice(0, n);
    var last = out.charCodeAt(out.length - 1);
    if (last >= 0xD800 && last <= 0xDBFF) out = out.slice(0, -1);
    return out;
  }

  function encode(cfg) {
    var c = pick(cfg, LINK_KEYS);
    if (typeof c.m === 'string') c.m = trimMessage(c.m, MAX_MSG);
    if (c.b || !c.m) { delete c.m; delete c.mt; }   /* a blank card carries no words */
    return toB64url(JSON.stringify(c));
  }

  function decode(str) {
    if (!str) return null;
    var obj;
    try {
      obj = JSON.parse(fromB64url(str));
    } catch (e) {
      return null;
    }
    if (!obj || typeof obj !== 'object') return null;
    var v = parseInt(obj.v, 10);
    if (!v || v > VERSION) return null;
    return pick(obj, LINK_KEYS);
  }

  function rawParam(search, name) {
    var parts = String(search || '').replace(/^\?/, '').split('&');
    for (var i = 0; i < parts.length; i++) {
      var kv = parts[i].split('=');
      if (decodeURIComponent(kv[0]) === name) {
        return decodeURIComponent(String(kv[1] || '').replace(/\+/g, ' '));
      }
    }
    return null;
  }

  function fromLocation(search) {
    return decode(rawParam(search, PARAM));
  }

  /* productUrl may be a path or absolute; any query or hash on it is
     dropped so the link is always <product>?config=...[&mb_edit=...]. */
  function build(productUrl, cfg, extra) {
    var base = String(productUrl || '').split('#')[0].split('?')[0];
    var tail = (extra && extra.mb_edit)
      ? '&mb_edit=' + encodeURIComponent(extra.mb_edit)
      : '';
    var c = pick(cfg, LINK_KEYS);
    var url = base + '?' + PARAM + '=' + encode(c) + tail;
    /* Too long for an email link: shorten the message a step at a time and
       say so (mt), so the message screen can ask the customer to check it.
       Everything else in the link is small and is never cut. */
    var steps = [200, 120, 60, 0];
    for (var i = 0; url.length > MAX_URL && typeof c.m === 'string' && i < steps.length; i++) {
      c.m = trimMessage(c.m, steps[i]);
      c.mt = 1;
      url = base + '?' + PARAM + '=' + encode(c) + tail;
    }
    return url;
  }

  /* ---- same-device memory ------------------------------------------- */

  function storageKey(handle) { return STORE_PREFIX + String(handle || ''); }

  function save(handle, cfg, step) {
    try {
      var rec = { t: Date.now(), cfg: pick(cfg, STORE_KEYS), step: step || null };
      window.localStorage.setItem(storageKey(handle), JSON.stringify(rec));
      return true;
    } catch (e) {
      return false;
    }
  }

  function load(handle) {
    try {
      var raw = window.localStorage.getItem(storageKey(handle));
      if (!raw) return null;
      var rec = JSON.parse(raw);
      if (!rec || !rec.cfg || !rec.t) return null;
      if (Date.now() - rec.t > TTL_DAYS * 864e5) { clear(handle); return null; }
      rec.cfg = pick(rec.cfg, STORE_KEYS);
      return rec;
    } catch (e) {
      return null;
    }
  }

  function clear(handle) {
    try { window.localStorage.removeItem(storageKey(handle)); } catch (e) { /* private mode */ }
  }

  window.MBConfigLink = {
    VERSION: VERSION,
    PARAM: PARAM,
    TTL_DAYS: TTL_DAYS,
    MAX_MSG: MAX_MSG,
    MAX_URL: MAX_URL,
    encode: encode,
    decode: decode,
    build: build,
    rawParam: rawParam,
    fromLocation: fromLocation,
    save: save,
    load: load,
    clear: clear
  };
})();
